// @flow

import React from "react";
import pick from "lodash/pick";

import {
  PageHeader,
  EditableForm,
  FormRow,
  EditableField,
  formStyles,
  Preloader,
  FieldControl,
  validators as v,
  errors,
  FlashMessageConsumer,
  Card,
  ChangePassword,
  Section
} from "buba-components";
import { PHONE_MAX_LENGTH } from "buba-components/src/data/formFields/validationConfig";
import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import DataFetcher from "buba-components/src/components/DataFetcher";
import PageLayout from "buba-components/src/components/PageLayout";
import { isBankAdvisorPasswordRegex } from "buba-components/src/utils/regexes";
import { Flag, PASSWORD_RESET } from "buba-components/src/utils/featureFlags";

import { getUserData, updateUserData, changePassword } from "../../api/actions";

import styles from "./UserData.scss";
import {
  FIRST_NAME,
  LAST_NAME,
  POSITION,
  PHONE_NUMBER,
  EMAIL,
  BANK_CODE,
  BANK_NAME,
  GENDER
} from "./formFields";

import genderOptions from "./genderOptions";

const update = (fetchUpdated: any => void, onError: string => void) => values => {
  const payload = pick(values, GENDER, FIRST_NAME, LAST_NAME, PHONE_NUMBER);
  return updateUserData(payload)
    .then(() => fetchUpdated({}))
    .catch(() => {
      onError(
        "Beim Aktualisieren Ihrer Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."
      );
      fetchUpdated({});
    });
};

const UserData = () => (
  <PageLayout>
    <PageHeader header="Meine Daten" bottomMargin />
    <FlashMessageConsumer>
      {({ addMessage }) => (
        <DataFetcher
          apiAction={getUserData}
          mapper={denormalizeJsonApi}
          initialPayload={{}}
          onError={msg => addMessage(msg, "failure")}
        >
          {({ data: userData, isLoading: isUserDataLoading, requestFetch }) =>
            isUserDataLoading || !userData ? (
              <Card>
                <Preloader />
              </Card>
            ) : (
              <>
                <Section reducedMargin>
                  <EditableForm
                    onSubmit={update(requestFetch, msg => addMessage(msg, "failure"))}
                    initialValues={{
                      [BANK_CODE]: userData.bank.code,
                      [BANK_NAME]: userData.bank.name,
                      [GENDER]: userData.gender,
                      [EMAIL]: userData.email,
                      [FIRST_NAME]: userData.firstName,
                      [LAST_NAME]: userData.lastName,
                      [POSITION]: userData.position,
                      [PHONE_NUMBER]: userData.phoneNumber
                    }}
                  >
                    <div className={formStyles.FIELD_STYLE_SUMMARY}>
                      <FormRow separator>
                        <FieldControl caption={() => "BLZ"} name={BANK_CODE}>
                          {({ input }) => <div className={styles.value}>{input.value}</div>}
                        </FieldControl>
                        <FieldControl caption={() => "Bank"} name={BANK_NAME}>
                          {({ input }) => <div className={styles.value}>{input.value}</div>}
                        </FieldControl>
                        <div />
                      </FormRow>
                      <FormRow separator>
                        <EditableField
                          fieldControlProps={{
                            caption: () => "Anrede",
                            name: GENDER,
                            isOptional: true
                          }}
                          displayValue={value => {
                            const option = genderOptions.find(o => o.value === value);

                            return option && option.label;
                          }}
                          type="select"
                          inputProps={{ options: genderOptions }}
                        />
                        <EditableField
                          fieldControlProps={{
                            caption: () => "Vorname",
                            name: FIRST_NAME,
                            validate: v.required(errors.requiredField())
                          }}
                          type="textInput"
                        />
                        <EditableField
                          fieldControlProps={{
                            caption: () => "Nachname",
                            name: LAST_NAME,
                            validate: v.required(errors.requiredField())
                          }}
                          type="textInput"
                        />
                      </FormRow>
                      <FormRow>
                        <FieldControl caption={() => "E-Mail-Adresse"} name={EMAIL}>
                          {({ input }) => <div className={styles.value}>{input.value}</div>}
                        </FieldControl>
                        <EditableField
                          fieldControlProps={{
                            caption: () => "Telefon",
                            name: PHONE_NUMBER,
                            validate: v.optionalCombineValidators(
                              v.isPhoneNumber(errors.isPhoneNumberCorrect()),
                              v.maxLength(
                                PHONE_MAX_LENGTH,
                                errors.maxLengthCharacters(PHONE_MAX_LENGTH)
                              )
                            )
                          }}
                          type="textInput"
                        />
                        <div />
                      </FormRow>
                    </div>
                  </EditableForm>
                </Section>
                <Flag
                  name={[PASSWORD_RESET]}
                  render={() => (
                    <ChangePassword
                      passwordRegex={isBankAdvisorPasswordRegex}
                      passwordError={errors.isPasswordExtended()}
                      addMessage={addMessage}
                      changePasswordAction={changePassword}
                    />
                  )}
                />
              </>
            )
          }
        </DataFetcher>
      )}
    </FlashMessageConsumer>
  </PageLayout>
);

export default UserData;
